import { Link } from 'react-router-dom';
import {
  Brain, Target, Wallet, BarChart3, CreditCard, Zap, TrendingUp,
  Users, Bell, Shield, Download, RefreshCw, PieChart, Repeat,
  ArrowRight, Check, Star, Gift,
} from 'lucide-react';

const featureSections = [
  {
    title: 'Inteligência Artificial',
    subtitle: 'Seu consultor financeiro particular',
    description: 'Não somos só um app de controle. Nossa IA analisa seus padrões de consumo e age como um verdadeiro consultor financeiro — sem julgamentos, com dados reais.',
    color: 'from-purple-600 to-primary-600',
    icon: Brain,
    features: [
      { title: 'Análise de Perfil de Consumo', description: 'Identifica seus padrões de gasto e classifica seu perfil financeiro automaticamente.' },
      { title: 'Plano Personalizado de Metas', description: 'Você define o sonho, a IA calcula quanto guardar por mês e monitora o progresso.' },
      { title: 'Modo Resgate de Dívidas', description: 'Para quem está endividado: análise de dívidas, priorização e plano de quita.' },
      { title: 'Alertas Preditivos', description: 'Avisos antes de você estourar o orçamento, baseados no seu padrão de gasto.' },
      { title: 'Insights Semanais', description: 'Todo domingo, um resumo com o que você gastou e onde pode economizar.' },
    ],
  },
  {
    title: 'Gestão Financeira',
    subtitle: 'Controle total das suas finanças',
    description: 'De contas a categorias, parcelamentos a recorrências. Tudo centralizado, tudo organizado.',
    color: 'from-emerald-600 to-teal-600',
    icon: Wallet,
    features: [
      { title: 'Multi-contas', description: 'Bancos, carteiras digitais, cartões de crédito. Tudo em um painel unificado.' },
      { title: 'Categorização Inteligente', description: 'Categorias personalizáveis com ícones e cores. Subcategorias incluídas.' },
      { title: 'Parcelamentos', description: 'Registre compras parceladas e veja o impacto mês a mês no seu fluxo de caixa.' },
      { title: 'Recorrências', description: 'Assinaturas, aluguéis, mensalidades. Nunca mais esqueça uma conta fixa.' },
      { title: 'Orçamento por Categoria', description: 'Defina limites de gasto por categoria e receba alertas quando se aproximar.' },
    ],
  },
  {
    title: 'Relatórios e Análises',
    subtitle: 'Visibilidade total do seu dinheiro',
    description: 'Gráficos que contam histórias. Relatórios que revelam comportamentos que você não sabia que tinha.',
    color: 'from-blue-600 to-primary-600',
    icon: BarChart3,
    features: [
      { title: 'Evolução Patrimonial', description: 'Acompanhe o crescimento (ou queda) do seu patrimônio mês a mês.' },
      { title: 'Relatório por Categoria', description: 'Quanto você gastou em alimentação, transporte, lazer? Descubra.' },
      { title: 'Fluxo de Caixa', description: 'Projeção de entradas e saídas para os próximos meses, baseada em recorrências.' },
      { title: 'Comparativo Mensal', description: 'Compare meses lado a lado e veja sua evolução ao longo do tempo.' },
      { title: 'Exportação CSV/JSON', description: 'Exporte todos os seus dados quando quiser. Seus dados são seus.' },
    ],
  },
  {
    title: 'Investimentos',
    subtitle: 'Do controle ao crescimento',
    description: 'Não é só sobre gastar menos. É sobre construir patrimônio. Acompanhe seus investimentos e simule o futuro.',
    color: 'from-orange-500 to-rose-600',
    icon: TrendingUp,
    features: [
      { title: 'Portfólio Unificado', description: 'Renda fixa, variável, FIIs, criptomoedas. Tudo em um só lugar.' },
      { title: 'Simulador de Juros Compostos', description: 'Veja quanto um investimento mensal pode virar em 10, 20 ou 30 anos.' },
      { title: 'Registro de Aportes', description: 'Histórico completo de compras e dividendos recebidos.' },
      { title: 'Projeção de Carteira', description: 'Estime o crescimento do portfólio com diferentes cenários de rentabilidade.' },
      { title: 'Cotações em Tempo Real', description: 'Acompanhe IBOVESPA, dólar, euro e principais índices no topo do painel.' },
    ],
  },
  {
    title: 'Simulação e Cenários',
    subtitle: 'Decida com dados, não com intuição',
    description: 'Teste qualquer decisão financeira sem riscos. O simulador cria um ambiente paralelo para você experimentar.',
    color: 'from-yellow-500 to-orange-500',
    icon: Zap,
    features: [
      { title: 'Modo Simulação', description: 'Um ambiente paralelo à sua conta real para testar cenários sem afetar seus dados.' },
      { title: 'Eventos Financeiros', description: 'Simule a compra de uma casa, um aumento de renda, um novo carro.' },
      { title: 'Comparação de Cenários', description: 'Compare dois caminhos lado a lado: "e se eu comprar?" vs "e se eu alugar?"' },
      { title: 'Impacto a Longo Prazo', description: 'Veja como uma decisão de hoje afeta seu patrimônio em 5, 10 e 20 anos.' },
    ],
  },
  {
    title: 'Família e Colaboração',
    subtitle: 'Finanças que unem, não que dividem',
    description: 'Planejamento financeiro em família requer transparência e confiança. O MyBank foi pensado para isso.',
    color: 'from-pink-500 to-rose-600',
    icon: Users,
    features: [
      { title: 'Até 5 usuários', description: '1 proprietário + até 4 membros. Inclua cônjuge, filhos, pais. Cada um com seu próprio login.' },
      { title: 'Permissões por membro', description: 'Escolha se cada membro pode só ver ou também editar transações.' },
      { title: 'Contas compartilhadas', description: 'Contas que todos os membros da família podem visualizar e registrar.' },
      { title: 'Dashboard familiar', description: 'Uma visão consolidada das finanças de toda a família.' },
      { title: 'Metas conjuntas', description: 'Criem metas familiares e acompanhem o progresso juntos.' },
    ],
  },
  {
    title: 'Gamificacao e Indicacoes',
    subtitle: 'Financas que recompensam',
    description: 'Organize suas financas e seja recompensado por isso. Ganhe XP, suba de nivel, desbloqueie conquistas e ganhe dinheiro indicando amigos.',
    color: 'from-yellow-500 to-orange-600',
    icon: Star,
    features: [
      { title: 'Sistema de niveis', description: '10 niveis de evolucao — de Iniciante a Lenda Financeira. Ganhe XP a cada acao.' },
      { title: 'Streaks diarios', description: 'Mantenha uma sequencia de dias ativos e ganhe XP bonus a cada dia.' },
      { title: '15 conquistas', description: 'Desbloqueie badges exclusivos: Maratonista, Embaixador, Orcamento Ninja e mais.' },
      { title: 'Programa de indicacao', description: 'Convide amigos e ganhe comissoes recorrentes: 9% nivel 1, 5% nivel 2, 1% nivel 3.' },
      { title: 'Saque via Pix', description: 'Acumule comissoes e saque a partir de R$50,00 direto na sua conta.' },
    ],
  },
];

const allHighlights = [
  'Autenticação em dois fatores (2FA)',
  'Dados criptografados (AES-256)',
  'Backup automático na nuvem',
  'Modo claro e escuro',
  'Atalhos de teclado',
  'Busca global com Ctrl+K',
  'Importação de extratos OFX',
  'Notificações por e-mail',
  'Suporte em português',
  'LGPD compliant',
  'Zero acesso a sua conta bancária',
  'Cancele quando quiser',
];

const Features = () => {
  return (
    <div className="bg-white dark:bg-gray-950">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary-400 font-semibold text-sm uppercase tracking-widest mb-4">Funcionalidades</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Tudo que você precisa.<br />
            <span className="text-primary-400">Nada que você não precisa.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Desenvolvemos cada funcionalidade resolvendo um problema real. Sem inchaço,
            sem funcionalidades de vitrine. Só o que funciona.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-semibold transition-colors shadow-lg shadow-primary-600/30"
          >
            Testar grátis agora
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* Feature sections */}
      {featureSections.map((section, idx) => {
        const Icon = section.icon;
        const isEven = idx % 2 === 0;
        return (
          <section key={section.title} className={`py-20 ${isEven ? 'bg-white dark:bg-gray-950' : 'bg-gray-50 dark:bg-gray-900'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid lg:grid-cols-2 gap-16 items-center ${isEven ? '' : 'lg:grid-flow-dense'}`}>
                {/* Text */}
                <div className={isEven ? '' : 'lg:col-start-2'}>
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-r ${section.color} bg-opacity-10 mb-5`}>
                    <Icon size={16} className="text-white" />
                    <span className="text-white text-xs font-bold uppercase tracking-wide">{section.title}</span>
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
                    {section.subtitle}
                  </h2>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
                    {section.description}
                  </p>
                  <ul className="space-y-4">
                    {section.features.map((f) => (
                      <li key={f.title} className="flex gap-3">
                        <div className="w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={12} className="text-primary-600" />
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900 dark:text-white text-sm">{f.title}</span>
                          <span className="text-gray-500 dark:text-gray-400 text-sm"> — {f.description}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                  <div className={`rounded-3xl p-10 bg-gradient-to-br ${section.color} relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                    <div className="relative flex flex-col items-center justify-center gap-4 py-8">
                      <Icon size={64} className="text-white opacity-90" />
                      <p className="text-white text-2xl font-bold text-center">{section.title}</p>
                      <p className="text-white/70 text-center text-sm max-w-xs">{section.subtitle}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Extras */}
      <section className="py-20 bg-white dark:bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">E muito mais</h2>
            <p className="text-xl text-gray-500 dark:text-gray-400">Os detalhes que fazem a diferença no dia a dia.</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {allHighlights.map((item) => (
              <div key={item} className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
                <Check size={16} className="text-emerald-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Pronto para começar?</h2>
          <p className="text-xl text-gray-400 mb-10">Plano gratuito para sempre. Sem cartão de crédito.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-semibold transition-colors shadow-lg shadow-primary-600/25">
              Criar conta grátis <ArrowRight size={18} />
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white rounded-xl font-semibold transition-colors">
              Ver planos e preços
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
